import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Home, Film, Tv, Music, User, LogOut, Menu, X, Heart, Crown, Shield } from 'lucide-react';

const Navbar = () => {
  const { user, isAuthenticated, signout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleSignout = async () => {
    await signout();
    navigate('/');
  };

  const navItems = [
    { path: '/', label: 'Home', icon: Home },
    { path: '/movies', label: 'Movies', icon: Film },
    { path: '/tv-shows', label: 'TV Shows', icon: Tv },
    { path: '/music-videos', label: 'Videos', icon: Music },
  ];

  const userNavItems = [
    { path: '/favorites', label: 'Favorites', icon: Heart },
    { path: '/profile', label: 'Profile', icon: User },
    ...(!user?.is_premium ? [{ path: '/subscription', label: 'Upgrade', icon: Crown, highlight: true }] : []),
    ...(user?.is_admin ? [{ path: '/admin', label: 'Admin', icon: Shield, adminOnly: true }] : []),
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Desktop Sidebar */}
      <nav className="hidden md:flex fixed left-0 top-0 bottom-0 w-1/5 min-w-[240px] max-w-[300px] bg-background/95 backdrop-blur-xl border-r border-white/10 shadow-2xl shadow-black/50 z-50 flex-col">
        <div className="flex flex-col h-full p-6">
          {/* Logo */}
          <Link to="/" className="flex items-center justify-center mb-8 group">
            <img 
              src="https://www.pulseportal.tv/api/files/pulsemedia/logos/pulse-media-9866ad29-95f6-4b54-af38-f238ec5caee9.png" 
              alt="Pulse Media" 
              className="h-10 w-auto transition-transform group-hover:scale-105"
            />
          </Link>

          {/* Spacer to push nav items to center */}
          <div className="flex-1"></div>

          {/* Navigation Links - Centered */}
          <div className="space-y-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-xl transition-all ${
                    active
                      ? 'bg-electric text-background shadow-lg shadow-electric/30'
                      : 'text-foreground/80 hover:bg-white/10 hover:text-electric'
                  }`}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <Icon className={`h-5 w-5 ${active ? '' : 'group-hover:scale-110 transition-transform'}`} />
                  <span className="font-medium">{item.label}</span>
                </Link>
              );
            })}

            {/* User Nav Items (only if authenticated) */}
            {isAuthenticated && (
              <>
                <div className="h-px bg-white/10 my-4"></div>
                {userNavItems.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.path);
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`flex items-center space-x-3 px-4 py-3 rounded-xl transition-all ${
                        active
                          ? 'bg-electric text-background shadow-lg shadow-electric/30'
                          : item.adminOnly
                          ? 'text-purple-400 border border-purple-400/30 hover:bg-purple-400/10'
                          : item.highlight
                          ? 'text-electric border border-electric/30 hover:bg-electric/10'
                          : 'text-foreground/80 hover:bg-white/10 hover:text-electric'
                      }`}
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <Icon className={`h-5 w-5 ${active ? '' : 'group-hover:scale-110 transition-transform'}`} />
                      <span className="font-medium">{item.label}</span>
                    </Link>
                  );
                })}
              </>
            )}
          </div>

          {/* Spacer to push user section to bottom */}
          <div className="flex-1"></div>

          {/* User Section */}
          <div className="border-t border-white/10 pt-6 mt-8">
            {isAuthenticated ? (
              <div className="space-y-3">
                <div className="flex items-center space-x-3 px-4 py-3 rounded-xl bg-white/5">
                  <div className="w-8 h-8 rounded-full bg-electric flex items-center justify-center">
                    <User className="h-4 w-4 text-background" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{user?.username}</p>
                    <p className="text-xs text-foreground/60">{user?.is_premium ? 'Premium' : 'Member'}</p>
                  </div>
                </div>
                <button
                  onClick={handleSignout}
                  className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-foreground/80 hover:bg-white/10 hover:text-electric transition-all"
                  data-testid="signout-button"
                >
                  <LogOut className="h-5 w-5" />
                  <span className="font-medium">Sign Out</span>
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <Link
                  to="/signin"
                  className="block w-full px-4 py-3 rounded-xl text-center text-foreground/80 hover:bg-white/10 hover:text-electric transition-all font-medium"
                  data-testid="signin-link"
                >
                  Sign In
                </Link>
                <Link
                  to="/signup"
                  className="block w-full px-4 py-3 rounded-xl text-center bg-electric text-background font-medium hover:bg-electric/90 transition-all shadow-lg shadow-electric/30"
                  data-testid="signup-link"
                >
                  Sign Up
                </Link>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Mobile Top Bar */}
      <nav className="md:hidden fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-xl border-b border-white/10 shadow-lg">
        <div className="px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img 
              src="https://www.pulseportal.tv/api/files/pulsemedia/logos/pulse-media-9866ad29-95f6-4b54-af38-f238ec5caee9.png" 
              alt="Pulse Media" 
              className="h-8 w-auto"
            />
          </Link>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 rounded-lg hover:bg-white/10 transition-colors"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="absolute top-full left-0 right-0 bg-background/95 backdrop-blur-xl border-b border-white/10 p-4 space-y-2 shadow-xl">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-xl transition-all ${
                    active
                      ? 'bg-electric text-background'
                      : 'text-foreground/80 hover:bg-white/10'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="font-medium">{item.label}</span>
                </Link>
              );
            })}

            <div className="border-t border-white/10 pt-4 mt-4">
              {isAuthenticated ? (
                <>
                  <div className="flex items-center space-x-3 px-4 py-3 mb-2">
                    <User className="h-5 w-5 text-electric" />
                    <span className="text-sm text-foreground">{user?.username}</span>
                  </div>
                  <button
                    onClick={() => {
                      handleSignout();
                      setMobileMenuOpen(false);
                    }}
                    className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-foreground/80 hover:bg-white/10 transition-all"
                  >
                    <LogOut className="h-5 w-5" />
                    <span className="font-medium">Sign Out</span>
                  </button>
                </>
              ) : (
                <div className="space-y-2">
                  <Link
                    to="/signin"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block w-full px-4 py-3 rounded-xl text-center text-foreground/80 hover:bg-white/10 transition-all"
                  >
                    Sign In
                  </Link>
                  <Link
                    to="/signup"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block w-full px-4 py-3 rounded-xl text-center bg-electric text-background font-medium hover:bg-electric/90 transition-all"
                  >
                    Sign Up
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </nav>
    </>
  );
};

export default Navbar;
